import { productConstants } from '@/constants'
import Toggles from '@domain/toggles/Toggles'
import { SUBFAMILIES_ID } from '@domain/data/Constants'
import { isArticleConfigured } from '@domain/documents/specSheetVariant'

// Subfamilies whose composition's accessories must NOT be added to the techSheetComposition zip.
const SUBFAMILIES_WITHOUT_ACCESSORY_TECH_SHEETS = [SUBFAMILIES_ID.PLUSMINUS, SUBFAMILIES_ID.STICKS]

export const addAttrSpecSheet = (product, projectInfo) => {
	let optionAttrProduct = { type: 'specSheet' }

	switch (product?.type) {
		case productConstants.PRODUCT_TYPE_REGULAR:
			if (product?.isPersonalized) {
				optionAttrProduct = {
					...optionAttrProduct,
					subType: 'reference',
					params: {
						productType: product?.type,
						productId: product?._id,
						projectId: projectInfo?.id,
						isPersonalized: product?.isPersonalized,
						projectName: projectInfo?.name,
						familyId: product?.familyId,
						subFamily: product?.subFamily,
						reference: product?.referenceCode
					}
				}
			} else {
				optionAttrProduct = {
					...optionAttrProduct,
					subType: 'model',
					params: {
						productType: product?.type,
						productId: product?._id,
						familyId: product?.familyId,
						subFamily: product?.subFamily,
						model: product?.model
					}
				}
			}
			return optionAttrProduct

		case productConstants.PRODUCT_TYPE_CREATION:
			return (optionAttrProduct = {
				type: 'creationSpecSheet',
				subType: product?.isPersonalized ? 'creation' : 'umbrella',
				params: {
					projectId: projectInfo?.id,
					catalogId: projectInfo?.catalogId,
					currency: projectInfo?.currency,
					product: {
						subFamily: product?.subFamily,
						isPersonalized: product?.isPersonalized,
						productType: product?.type,
						creationCode: product?.creationCode,
						familyId: product?.familyId,
						model: product?.model
					}
				}
			})

		default:
			return optionAttrProduct
	}
}

export const addAttrInstallation = (product, projectInfo, lang) => {
	let optionAttrProduct = { type: 'manual' }

	if (product?.type === productConstants.PRODUCT_TYPE_REGULAR) {
		return (optionAttrProduct = {
			...optionAttrProduct,
			subType: productConstants.PRODUCT_TYPE_REGULAR,
			params: {
				productType: product?.type,
				familyId: product?.familyId,
				subFamilyId: product?.subFamily?.id,
				applicationLocationId: product?.applicationLocation.id,
				model: product?.model,
				projectId: projectInfo?.id
			}
		})
	} else {
		return (optionAttrProduct = {
			...optionAttrProduct,
			subType: 'manualCreation',
			params: {
				productType: product?.type,
				creationCode: product?.creationCode,
				projectId: projectInfo?.id,
				familyId: product?.familyId,
				subFamilyId: product?.subFamily?.id,
				creationNumber: product?.creationNumber,
				language: lang,
				configuratorType: product.configuratorType.description
			}
		})
	}
}

export const addAttrEnergyLabel = (product, projectInfo) => ({
	type: 'energyLabel',
	params: {
		model: product?.model || product?.compositions[0]?.articles[0]?.model || '',
		familyId: product?.familyId,
		projectId: projectInfo?.id,
		subFamilyId: product?.subFamily.id
	}
})

export const addAttrModel3d = (product, projectInfo) => {
	const isCompositionDownloadEnabled = Toggles.isEnabled("download2D3DInComposition")
	const isCreationWithCompositions = product?.type === productConstants.PRODUCT_TYPE_CREATION && product?.compositions?.length > 0

	if (isCompositionDownloadEnabled && isCreationWithCompositions) {
		const models = []
		const uniqueModels = new Set()

		const createModelObject = (model) => ({
			type: '2D_3D',
			params: {
				familyId: product?.familyId,
				subFamilyId: product?.subFamily?.id,
				applicationLocationId: product?.applicationLocation.id,
				model,
				projectId: projectInfo?.id
			}
		})

		const processItems = (items) => {
			items?.forEach((item) => {
				if (item?.model && !uniqueModels.has(item.model)) {
					uniqueModels.add(item.model)
					models.push(createModelObject(item.model))
				}
			})
		}

		product.compositions.forEach((composition) => {
			processItems(composition?.articles)
			processItems(composition?.accessories)
		})

		return models
	}

	return {
		type: '2D_3D',
		params: {
			familyId: product?.familyId,
			subFamilyId: product?.subFamily?.id,
			applicationLocationId: product?.applicationLocation.id,
			model: product?.model || product?.compositions?.[0]?.articles?.[0]?.model || '',
			projectId: projectInfo?.id
		}
	}
}

export const addAttrTechSheetComposition = (product, projectInfo) => {
	const compositionSheet = {
		...addAttrSpecSheet(product, projectInfo),
		docname: `${product?.creationCode || 'composition'}.techSheetComposition`,
	}

	const articles = []
	const uniqueRefs = new Set()
	const includeAccessories = !SUBFAMILIES_WITHOUT_ACCESSORY_TECH_SHEETS.includes(product?.subFamily?.id)

	product.compositions?.forEach((composition) => {
		const compositionItems = [
			...(composition?.articles ?? []),
			...(includeAccessories ? (composition?.accessories ?? []) : []),
		]
		compositionItems.forEach((item) => {
			const ref = item?.referenceCode || item?.reference
			const configured = isArticleConfigured(item)
			// DV-6520: an unconfigured article carries its model as reference, which is not
			// a sellable product — asking sr-documents for that reference fails and the user
			// gets "file not available". Request those by model so the backend can serve the
			// static sheet or generate the model one.
			const key = configured ? ref : item?.model
			if (!key || uniqueRefs.has(key)) return
			uniqueRefs.add(key)

			articles.push({
				type: 'specSheet',
				subType: configured ? 'reference' : 'model',
				docname: `${key}.techSheetComposition`,
				params: {
					productType: productConstants.PRODUCT_TYPE_REGULAR,
					...(item._id && { productId: item._id }),
					projectId: projectInfo?.id,
					isPersonalized: configured,
					projectName: projectInfo?.name,
					familyId: item.familyId,
					subFamily: item.subFamily,
					// Sólo uno de los dos: en sr-documents `isModel = !!model`, así que enviar
					// `model` junto a la referencia degrada la ficha de referencia a ficha de
					// modelo genérica (regular.specSheet.utils.ts:13).
					...(configured ? { reference: ref } : { model: item?.model }),
				}
			})
		})
	})

	return [compositionSheet, ...articles]
}

export const addAttrProductHD = (product, subType, projectInfo) => ({
	type: 'imagesHD',
	subType,
	params: {
		familyId: product?.familyId,
		subFamilyId: product?.subFamily?.id,
		applicationLocationId: product?.applicationLocation.id,
		model: product?.model || product?.compositions[0]?.articles[0]?.model || '',
		projectId: projectInfo?.id
	}
})

export const addAttrPhotometry = (product, projectInfo) => ({
	type: 'photometry',
	params: {
		familyId: product?.familyId,
		subFamilyId: product?.subFamily?.id,
		applicationLocationId: product?.applicationLocation.id,
		model: product?.model || product?.compositions[0]?.articles[0]?.model || '',
		projectId: projectInfo?.id
	}
})
